import { useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import Typography, { TypographyProps } from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import MaterialEditIcon from '@mui/icons-material/Edit';

import { dateFormat } from '@bloomays-lib/utils.shared';
import { useAuthStore } from '@bloomays-lib/common.auth/browser';
import { TalentBoardComment as TalentBoardCommentType } from '@bloomays-lib/types.shared';
import { IconButton } from '@bloomays-lib/ui.shared';

import { TalentBoardCommentForm } from './TalentBoardCommentForm';
import { DeleteCommentButton } from '../atoms/DeleteCommentButton';

type TalentBoardCommentProps = {
  comment: TalentBoardCommentType;
  isClient: boolean;
};

export const TalentBoardComment = ({ comment, isClient }: TalentBoardCommentProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const { t } = useTranslation(['random']);
  const auth = useAuthStore((state) => state.auth);
  const isClientComment = !comment.author.includes('@bloomays.com');

  const isOwner = comment.author === auth?.user?.email;

  const isModified = comment.updatedAt !== comment.createdAt;

  return (
    <Comment isClientComment={isClientComment}>
      <CommentInfos>
        <CommentAuthor>{comment.author}</CommentAuthor>
        <CommentDate>
          {dateFormat(comment.createdAt, 'dd/MM/yyyy HH:mm')}
          {isModified && ` (${t('edited', { ns: 'random' })})`}
        </CommentDate>
      </CommentInfos>
      {!isEditing && <CommentValue key={comment.id}>{comment.comment}</CommentValue>}
      {isEditing && (
        <TalentBoardCommentForm
          talentId={comment.talentId}
          jobId={comment.jobId}
          isClient={isClient}
          comment={comment}
          setIsEditing={setIsEditing}
        />
      )}

      {isOwner && !isEditing && (
        <ButtonsContainer>
          <IconButtonEdit onClick={() => setIsEditing(true)} variant="contained">
            <EditIcon />
            <EditText>{t('modify', { ns: 'random' })}</EditText>
          </IconButtonEdit>
          <DeleteCommentButton comment={comment} />
        </ButtonsContainer>
      )}
    </Comment>
  );
};

const Comment = styled('div', { shouldForwardProp: (prop) => prop !== 'isClientComment' })<{
  isClientComment: boolean;
}>(({ theme, isClientComment }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  padding: '10px',
  borderRadius: '5px',
  alignItems: 'flex-start',
  backgroundColor: isClientComment ? theme.palette.blue[20] : theme.palette.neutral[10],
}));

const CommentValue: FC<TypographyProps> = styled(Typography)(() => ({
  fontSize: '14px',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
}));

const CommentInfos = styled('div')(() => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
}));

const CommentAuthor: FC<TypographyProps> = styled(Typography)(() => ({
  fontSize: '12px',
  fontFamily: 'Poppins Medium',
}));

const CommentDate: FC<TypographyProps> = styled(Typography)(() => ({
  fontSize: '10px',
}));

const EditIcon = styled(MaterialEditIcon)(({ theme }) => ({
  color: theme.palette.neutral.black,
  width: '12px',
  height: '12px',
}));

const EditText: FC<TypographyProps> = styled(Typography)(({ theme }) => ({
  fontSize: '10px',
  color: theme.palette.neutral.black,
  textDecoration: 'underline',
  cursor: 'pointer',
}));

const ButtonsContainer = styled('div')(() => ({
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
}));

const IconButtonEdit = styled(IconButton)(() => ({
  padding: '0',
  display: 'flex',
  gap: '4px',
}));
